import { useState } from "react";
import { Alert, Badge, Group, Stack, Tabs, Text } from "@mantine/core";
import {
  IconArrowsSort,
  IconInfoCircle,
  IconLayoutGrid,
  IconTargetArrow,
} from "@tabler/icons-react";
import { SeedingOrderTab } from "./SeedingOrderTab";
import { SeedingGroupPinTab } from "./SeedingGroupPinTab";
import { SeedingSlotPinTab } from "./SeedingSlotPinTab";
import type {
  SeedingEntrySlot,
  SeedingGroup,
  SeedingPinState,
  SeedingTeam,
} from "./seeding.types";

interface PresetPinningSupport {
  readonly supportsPinning: boolean;
  readonly supportsGroupPinning: boolean | null;
  readonly supportsSlotPinning: boolean | null;
}

interface SeedingPanelProps {
  readonly stepId: string;
  readonly preset: PresetPinningSupport;
  readonly teams: readonly SeedingTeam[];
  readonly groups: readonly SeedingGroup[];
  readonly entrySlots: readonly SeedingEntrySlot[];
  readonly seedingOrder: readonly string[];
  readonly initialPins: readonly SeedingPinState[];
  readonly onOrderSaved?: (orderedTeamIds: readonly string[]) => void;
  readonly onPinsSaved?: (pins: readonly SeedingPinState[]) => void;
  readonly onTeamWithdrawn?: (teamId: string) => void;
  readonly canWithdraw?: boolean;
}

type SeedingTab = "order" | "group" | "slot";

const isSeedingTab = (v: string | null): v is SeedingTab =>
  v === "order" || v === "group" || v === "slot";

export const SeedingPanel = ({
  stepId,
  preset,
  teams,
  groups,
  entrySlots,
  seedingOrder,
  initialPins,
  onOrderSaved,
  onPinsSaved,
  onTeamWithdrawn,
  canWithdraw = true,
}: SeedingPanelProps) => {
  const [activeTab, setActiveTab] = useState<SeedingTab>("order");

  const showGroupPin =
    preset.supportsPinning && preset.supportsGroupPinning === true;
  const showSlotPin =
    preset.supportsPinning && preset.supportsSlotPinning === true;

  return (
    <Stack gap="md">
      <Alert
        variant="light"
        color="blue"
        icon={<IconInfoCircle size={16} />}
        title="Seeding workflow"
      >
        <Text size="sm">
          Edit the seeding order and optional pins, save your changes, then
          apply them to the bracket with <strong>Seed step</strong> (while in
          GENERATED status) or <strong>Re-seed step</strong> (while in SEEDED
          status).
        </Text>
        <Group gap="xs" mt="xs">
          <Badge color={preset.supportsPinning ? "cyan" : "gray"} variant="light">
            {preset.supportsPinning ? "Pinning supported" : "Pinning not supported"}
          </Badge>
          {showGroupPin && (
            <Badge color="cyan" variant="light">
              Group pinning
            </Badge>
          )}
          {showSlotPin && (
            <Badge color="cyan" variant="light">
              Slot pinning
            </Badge>
          )}
        </Group>
      </Alert>

      <Tabs
        value={activeTab}
        onChange={(v) => {
          if (isSeedingTab(v)) setActiveTab(v);
        }}
      >
        <Tabs.List>
          <Tabs.Tab value="order" leftSection={<IconArrowsSort size={14} />}>
            Order
          </Tabs.Tab>
          {showGroupPin && (
            <Tabs.Tab value="group" leftSection={<IconLayoutGrid size={14} />}>
              Group pins
            </Tabs.Tab>
          )}
          {showSlotPin && (
            <Tabs.Tab value="slot" leftSection={<IconTargetArrow size={14} />}>
              Slot pins
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="order" pt="md">
          <SeedingOrderTab
            stepId={stepId}
            teams={teams}
            seedingOrder={seedingOrder}
            onOrderSaved={onOrderSaved}
            onTeamWithdrawn={onTeamWithdrawn}
            canWithdraw={canWithdraw}
          />
        </Tabs.Panel>

        {showGroupPin && (
          <Tabs.Panel value="group" pt="md">
            <SeedingGroupPinTab
              stepId={stepId}
              teams={teams}
              groups={groups}
              initialPins={initialPins}
              onPinsSaved={onPinsSaved}
            />
          </Tabs.Panel>
        )}

        {showSlotPin && (
          <Tabs.Panel value="slot" pt="md">
            <SeedingSlotPinTab
              stepId={stepId}
              teams={teams}
              entrySlots={entrySlots}
              initialPins={initialPins}
              onPinsSaved={onPinsSaved}
            />
          </Tabs.Panel>
        )}
      </Tabs>
    </Stack>
  );
};
